/**
 * «Отправить» у группы «Планов» (разметка 28.09.26 06:44 п.1): заказ QR-папки
 * фабрике в WeChat — QR, Excel на китайском, текст. Факт отправки пишет сервер
 * в журнал `wechat_send`; здесь — кнопка и метка исхода.
 *
 * Клики и клавиши гасятся: кнопка стоит в заголовке группы, и нажатие не должно
 * её сворачивать (то же правило, что у ссылок рядом).
 */

import { RotateCw, Send } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { ApiFailure, apiFetch } from "@/lib/api";
import { bqdHref } from "@/lib/orders";
import { isBusy, techcardMark, wechatMark, type WechatState } from "@/lib/wechat";

const TONE = {
  ok: "text-ok bg-ok-bg border-ok/25",
  err: "text-err bg-err-bg border-err/25",
  muted: "text-fg-muted bg-neutral-bg border-border",
} as const;

const BTN =
  "flex h-[21px] flex-none items-center gap-1 rounded border border-border-strong px-1.5 text-[11.5px] " +
  "text-fg-muted hover:border-accent hover:text-accent-text disabled:opacity-50";

export function WechatSend({
  qrId,
  state,
  onState,
}: {
  qrId: number;
  state: WechatState | undefined;
  onState: (state: WechatState) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const mark = wechatMark(state);
  const cardMark = techcardMark(state);

  const send = async (again: boolean) => {
    if (busy) return;
    // Получателя до первой отправки экран не знает (тестовый режим — настройка
    // сервера), поэтому первое подтверждение его не называет, чтобы не соврать.
    const куда = state ? ` (${state.chat ?? "поставщику"})` : "";
    const ask = again
      ? state?.status === "unverified"
        ? `Прошлая отправка bqd-${qrId} НЕ ПОДТВЕРЖДЕНА — она могла уйти. Проверили чат и всё равно отправить ещё раз?`
        : `Отправить заказ bqd-${qrId} в WeChat ещё раз${куда}?`
      : `Отправить заказ bqd-${qrId} в WeChat: QR, Excel, текст?`;
    // Техкарты идут следом за заказом: повтор соберёт их заново.
    const техкарты = cardMark
      ? "\nТехкарты будут собраны заново и отправлены после того, как заказ отправится."
      : "";
    if (!window.confirm(ask + техкарты)) return;
    setBusy(true);
    setFail(null);
    try {
      onState(
        await apiFetch<WechatState>(`/api/plans/qr/${qrId}/wechat`, {
          method: "POST",
          body: JSON.stringify({ again }),
        }),
      );
    } catch (error) {
      setFail(error instanceof ApiFailure ? error.message : "сервис не ответил");
    } finally {
      setBusy(false);
    }
  };

  const stop = {
    onClick: (event: React.MouseEvent) => event.stopPropagation(),
    onKeyDown: (event: React.KeyboardEvent) => event.stopPropagation(),
  };

  return (
    <span {...stop} className="flex flex-none items-center gap-1" data-annot={`WeChat ${qrId}`}>
      {mark ? (
        <span
          title={mark.title}
          className={
            "inline-flex h-[21px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-[11.5px] font-medium " +
            TONE[mark.tone]
          }
        >
          <span className="size-[5px] rounded-full bg-current" />
          {mark.text}
        </span>
      ) : (
        <button
          type="button"
          className={BTN}
          disabled={busy}
          onClick={() => void send(false)}
          title="отправить заказ фабрике в WeChat: картинка QR, Excel на китайском, текст"
        >
          <Send className="h-3 w-3" />
          {busy ? "отправляю…" : "Отправить"}
        </button>
      )}
      {mark && !isBusy(state) ? (
        <button
          type="button"
          className={BTN}
          disabled={busy}
          onClick={() => void send(true)}
          title="отправить заказ ещё раз — новой командой"
          aria-label={`отправить bqd-${qrId} ещё раз`}
        >
          <RotateCw className="h-3 w-3" />
        </button>
      ) : null}
      {cardMark ? (
        // Техкарты вслед за заказом (спека 2026-10-28-wechat-techcard-followup):
        // состояние словами или галочкой, не только цветом; пачки — в подсказке.
        <span
          title={cardMark.title}
          className={
            "inline-flex h-[21px] items-center whitespace-nowrap rounded-full border px-2 text-[11.5px] font-medium " +
            TONE[cardMark.tone]
          }
        >
          {cardMark.text}
        </span>
      ) : null}
      {state?.shortage ? (
        // Сообщения о недостаче по заказам папки (спека 2026-09-29-order-shortage-wechat):
        // сами сообщения — в карточках заказов, их список — на карточке BQD.
        <Link
          to={bqdHref(qrId)}
          className="whitespace-nowrap text-[11.5px] text-err hover:underline"
          title="по заказам этой папки поставщику писали о недостаче — журнал в карточке заказа"
        >
          +{state.shortage} недост.
        </Link>
      ) : null}
      {fail ? (
        <span className="max-w-[260px] truncate text-[11.5px] text-err" title={fail}>
          {fail}
        </span>
      ) : null}
    </span>
  );
}
